import { useCallback, useState } from "react";

import { Row, Table as TableT } from "@tanstack/react-table";
import { CanceledError } from "axios";
import { isBefore } from "date-fns";
import { useSearchParams } from "react-router-dom";
import { twJoin } from "tailwind-merge";

import {
  DecisionHistoryRecordV2,
  getHistoryDecision,
  SingleDecisionQueryErrorReason,
} from "src/api/decisionHistoryV2/decisionHistoryQueries";
import { DecisionEnvironment } from "src/api/types";
import { HistoricalDecisionApiResponse } from "src/clients/history_v3";
import { getHistoryEmptyStateWording } from "src/flow/decisionHistory/SharedEmptyStateWording";
import {
  DecisionStatusCode,
  getDecisionStatus,
} from "src/flow/decisionHistory/SharedStatusColumn";
import { useAuthoringUIActions } from "src/flowContainer/AuthoringUIContext";
import { useIsIcebergEnabled } from "src/hooks/useIsIcebergEnabled";
import { useJobs } from "src/jobs/api/queries";
import { usePowertools, useXrayTraceURL } from "src/powertools/hooks";
import { FEATURE_FLAGS, isFeatureFlagEnabled } from "src/router/featureFlags";
import { useAuthoringContext } from "src/router/routerContextHooks";
import { URLKeys } from "src/router/SearchParams";
import { AuthorPageParams } from "src/router/urls";
import { useNodeHighlighting } from "src/store/NodeHighlighting";
import { getNodeRunState } from "src/store/runState/RunState";
import { useGraphStore } from "src/store/StoreProvider";
import { convertToUUIDv7, getUUIDVersion } from "src/utils/convertToUUIDv7";
import { logger } from "src/utils/logger";
import { isHistoricalRunState } from "src/utils/predicates";
import { useParamsDecode } from "src/utils/useParamsDecode";
import { TableComp } from "src/versionDecisionHistory/BaseTable";
import {
  CONTROL_COLUMN_ID,
  getVersionDecisionHistoryColumns,
} from "src/versionDecisionHistory/Columns";
import { DecisionHistoryEmpty } from "src/versionDecisionHistory/Empty";
import {
  useHistorySelectedDecision,
  useSetHistorySelectedDecision,
} from "src/versionDecisionHistory/historySelectedDecisionStore";

const renderEmptyState = (
  singleDecisionError: SingleDecisionQueryErrorReason | undefined,
  emptyData: boolean,
) => {
  if (singleDecisionError || emptyData) {
    const wording = getHistoryEmptyStateWording(
      singleDecisionError ?? "emptyData",
    );
    return <DecisionHistoryEmpty {...wording} />;
  }
};

type Props = {
  statusCodeFilter: DecisionStatusCode | undefined;
  setStatusCodeFilter: (
    statusCodeFilter: DecisionStatusCode | undefined,
  ) => void;
  decisionHistory: DecisionHistoryRecordV2[] | undefined;
  workspaceUrl: string;
  isDraft: boolean;
  type: DecisionEnvironment;
  disableRowsBeforeDate?: string;
  disableRowsWithEtagSmallerThan?: string;
  onAddTestCase: (decisionId: string) => void;
  onCancelDecision: (flowSlug: string, decisionId: string) => void;
  cancellingDecisionIds?: string[];
  canFetchNextPage: boolean;
  fetchNextPage: () => void;
  isFetching: boolean;
  singleDecisionError?: SingleDecisionQueryErrorReason;
};

export type DecisionDisabledReason = "outdated" | "internal_error";

export const getDisableReasonMessage = (reason: DecisionDisabledReason) => {
  const mappings: Record<DecisionDisabledReason, string> = {
    outdated:
      "This decision cannot be displayed as the Decision Flow logic was in draft and has since been updated.",
    internal_error:
      "Decisions with status code 500 cannot be displayed due to an internal error. Our engineering team has been notified and will investigate the issue.",
  };
  return mappings[reason];
};

export const getDecisionDisabledReason = (
  decision: DecisionHistoryRecordV2 | HistoricalDecisionApiResponse,
  disableRowsBeforeEtag: string | undefined,
  disableRowsBeforeDate: string | undefined,
): DecisionDisabledReason | false => {
  if (decision.status_code === "500") {
    return "internal_error";
  }
  if (
    "is_sandbox" in decision
      ? !decision.is_sandbox
      : decision.environment === DecisionEnvironment.LIVE
  ) {
    return false;
  }
  const etag =
    "flow_version_etag" in decision
      ? decision.flow_version_etag
      : decision?.flow?.version?.etag;

  if (etag && disableRowsBeforeEtag) {
    return etag < disableRowsBeforeEtag ? "outdated" : false;
  }

  if (disableRowsBeforeDate) {
    return !decision.start_time ||
      isBefore(new Date(decision.start_time), new Date(disableRowsBeforeDate))
      ? "outdated"
      : false;
  }

  return false;
};

export const DecisionHistoryTable: React.FC<Props> = ({
  statusCodeFilter,
  setStatusCodeFilter,
  decisionHistory,
  workspaceUrl,
  isDraft,
  type,
  disableRowsBeforeDate,
  disableRowsWithEtagSmallerThan,
  onAddTestCase,
  onCancelDecision,
  cancellingDecisionIds,
  canFetchNextPage,
  fetchNextPage,
  isFetching,
  singleDecisionError,
}) => {
  const [_, setSearchParams] = useSearchParams();
  const {
    clearHistoryRunState,
    setSelectedNode,
    setHistoryRunState,
    selectErroredNode,
  } = useGraphStore();
  const [selectionStatus, setSelectionStatus] = useState<
    | { tag: "ready" | "active" }
    | { tag: "loading"; abortController: AbortController }
  >({ tag: "ready" });
  const [xrayTraceID, setXrayTraceID] = useState<string | null>(null);

  const { orgId, wsId } = useParamsDecode<AuthorPageParams>();
  const { flow, version, workspace } = useAuthoringContext();
  const { data: jobs, isInitialLoading: jobsQueryIsInitialLoading } = useJobs({
    baseUrl: workspace.base_url!,
    flowId: flow.id,
  });
  const { highlightNodes, clearHighlighting } = useNodeHighlighting();
  const isIcebergEnabled = useIsIcebergEnabled();
  const isPowertoolsEnabled = usePowertools();
  const xrayTraceURL = useXrayTraceURL(xrayTraceID);

  const currentDecision = useHistorySelectedDecision();
  const setCurrentDecision = useSetHistorySelectedDecision();

  const handleRowClick = (
    row: Row<DecisionHistoryRecordV2>,
    table: TableT<DecisionHistoryRecordV2>,
  ) => {
    if (selectionStatus.tag === "loading") {
      selectionStatus.abortController.abort();
    }

    const wasAlreadySelected = row.getIsSelected();
    // Clear run state
    if (wasAlreadySelected) {
      row.toggleSelected(false);
      setSelectionStatus({ tag: "ready" });
      setXrayTraceID(null);
      clearHistoryRunState();
      clearHighlighting();
      setSelectedNode(null);
      setCurrentDecision(null);
      setSearchParams((prevParams) => {
        prevParams.delete(URLKeys.DecisionId);
        return prevParams;
      });
      // handle the selection and load decision
    } else {
      table
        .getSelectedRowModel()
        .flatRows.forEach((row) => row.toggleSelected(false));
      row.toggleSelected(true);

      const abortController = new AbortController();
      setSelectionStatus({ tag: "loading", abortController });
      handleRowSelection(row.original, abortController.signal);
    }
  };

  const highlightNodesWithRunState = useCallback(
    (runState: DecisionHistoryRecordV2) => {
      if ("node_results" in runState) {
        const filteredNodes = version.graph?.nodes.filter((node) => {
          const runState = getNodeRunState(node.id, version.id);
          return runState && isHistoricalRunState(runState);
        });

        if (filteredNodes) {
          highlightNodes(filteredNodes?.map((node) => node.id));
        }
      }
    },
    [version, highlightNodes],
  );

  const { setSelectedResultsRow } = useAuthoringUIActions();

  const handleRowSelection = async (
    row: DecisionHistoryRecordV2,
    signal: AbortSignal,
  ) => {
    try {
      // Temporary solution to fetch with uuidv7 until supported natively
      // Remove as part of OPTI-3445
      const decisionId =
        isFeatureFlagEnabled(FEATURE_FLAGS.readDHv2) &&
        getUUIDVersion(row.id) !== 7
          ? convertToUUIDv7(row.id, new Date(row.start_time))
          : row.id;
      const decision = (
        await getHistoryDecision({
          baseUrl: workspaceUrl,
          decisionId: decisionId,
          // Only include additional fields when powertools are active
          includeExtraFields: isPowertoolsEnabled,
          signal,
          includeNodeResultsData: false,
          iceberg: isIcebergEnabled,
          flowId: row.flow.id,
          environment: type,
        })
      ).data;
      if (decision != null) {
        setCurrentDecision(decision);
        setHistoryRunState(decision);
        setSelectionStatus({ tag: "active" });
        setXrayTraceID(decision?.xray_trace_id ?? null);
        // When a decision history run is being shown, we want to highlight the nodes which were run
        highlightNodesWithRunState(decision);
        setSelectedResultsRow(null);
        setSearchParams((prevParams) => {
          prevParams.set(URLKeys.DecisionId, decision.id);
          return prevParams;
        });
      }
      if (getDecisionStatus(decision.status_code) === "success") {
        setSelectedNode(null);
      } else if (getDecisionStatus(decision.status_code) === "error") {
        selectErroredNode();
      }
    } catch (err) {
      setCurrentDecision(null);
      if (!(err instanceof CanceledError)) {
        logger.error(err);
      }
    }
  };

  const shouldRowBeDisabled = (
    record: DecisionHistoryRecordV2,
  ): "outdated" | "internal_error" | false => {
    return getDecisionDisabledReason(
      record,
      disableRowsWithEtagSmallerThan,
      disableRowsBeforeDate,
    );
  };

  if (type === "live" && isDraft) {
    return (
      <DecisionHistoryEmpty
        description="A record of decisions made by this particular version will show up here once the version is published."
        headline="This is a draft version and has no decision history yet."
      />
    );
  }
  const emptyData = Boolean(
    !isFetching && decisionHistory && decisionHistory.length === 0,
  );
  const showingGenericNotFound = emptyData && !isFetching;

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div
        className={twJoin(
          !showingGenericNotFound && !singleDecisionError && "min-h-0 flex-1",
        )}
      >
        <TableComp
          canFetchNextPage={canFetchNextPage}
          cellPropsGetter={(cell, row) => {
            const isSelected = row.getIsSelected();
            const isDisabled = shouldRowBeDisabled(row.original);
            const backgroundGrayed = isSelected || isDisabled;
            const controlColClasses = twJoin(
              "sticky right-0 z-0 box-border border-r-6",
              isSelected
                ? "border-r-indigo-400 bg-gray-50"
                : "border-r-transparent",
              backgroundGrayed
                ? "group-hover/row:bg-gray-50"
                : "group-hover/row:bg-white",
            );
            return {
              classNameOverrides:
                cell.column.id === CONTROL_COLUMN_ID
                  ? controlColClasses
                  : undefined,
            };
          }}
          columns={getVersionDecisionHistoryColumns({
            orgId,
            wsId,
            flow,
            jobs: jobs ?? [],
            version,
            env: type,
            statusCodeFilter,
            setStatusCodeFilter,
            selectedRowIsLoading: selectionStatus.tag === "loading",
            shouldRowBeDisabled,
            onAddTestCase,
            onCancelDecision,
            cancellingDecisionIds,
            isPowertoolsEnabled,
            xrayTraceURL,
          })}
          currentDecision={currentDecision}
          // Show the table only after we have fetched the jobs
          data={(!jobsQueryIsInitialLoading && decisionHistory) || []}
          dataLoc="decision-history-table"
          fetchNextPage={fetchNextPage}
          frameClassName={twJoin(
            "overflow-auto border-gray-200 border-t",
            emptyData && "h-16",
          )}
          handleRowClick={handleRowClick}
          headerCellPropsGetter={(column) => {
            return {
              classNameOverrides:
                column.id === CONTROL_COLUMN_ID
                  ? "sticky right-0 top-0 bg-white"
                  : undefined,
            };
          }}
          isFetching={isFetching || jobsQueryIsInitialLoading}
          rowPropsGetter={(row, table) => {
            const isSelected = row.getIsSelected();
            const isDisabled = shouldRowBeDisabled(row.original);

            return {
              "data-loc": `decision-history-row-${row.original.status_code}`,
              onClick: !isDisabled
                ? () => handleRowClick(row, table)
                : undefined,
              classNameOverrides: twJoin(
                "group/row",
                isSelected && "group/row-selected",
                !isDisabled && "cursor-pointer",
                (isSelected || isDisabled) && "bg-gray-50",
              ),
            };
          }}
          skeletonRowsClassnames={[
            "w-[35px] pl-3.5",
            "w-[90px]",
            "w-[100px]",
            "w-[150px]",
          ]}
        />
      </div>
      {renderEmptyState(singleDecisionError, emptyData)}
    </div>
  );
};
